import { useState, type ChangeEvent, type ReactNode } from "react";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { formatCzkWithDisclosure } from "../../lib/numberToWords";
import type {
  GenerateQrResponse,
  PushAssistUserDetail,
} from "../../schemas/pushAssist";

/**
 * UC-009 PushAssistDetailCard — Action Card pro Josefa.
 *
 * Layout:
 *   - Header: jméno + email + role + consent badge (Souhlas OK / Souhlas chybí).
 *   - Krok 0: papírový consent upload (file input + Nahrát button). Hard block
 *     pokud `has_valid_paper_consent === false` → tlačítka níže disabled.
 *   - "Generovat QR" button: zavolá onGenerateQr → callback s payment_intent_id.
 *   - Velký QR display 320×320 px + Stáhnout PNG button (only po generování).
 *   - Slovní popis částky (Disclosure) — formatCzkWithDisclosure pro telephonic mode.
 *   - Audit poznámka (textarea) + delivery_channel radio + Doručit button.
 *
 * Accessibility:
 *   - Touch targets ≥ 48 × 48 px (gate per ANALYZE §5.9.2).
 *   - Visible focus ring; "Generovat QR" disabled má aria-disabled + tooltip.
 *   - Banner "Consent odvolán" má aria-live=assertive (AT-017 path).
 */

export interface PushAssistDetailCardProps {
  detail: PushAssistUserDetail;
  paymentIntentId?: string;
  amountKc?: number;
  qr?: GenerateQrResponse;
  uploadError?: string | null;
  generateError?: string | null;
  uploading?: boolean;
  generating?: boolean;
  withdrawn?: boolean;
  onUploadConsent: (file: File) => void;
  onGenerateQr: (paymentIntentId: string) => void;
  onLogDelivery: (input: {
    delivery_channel: "osobni" | "email" | "whatsapp" | "telefon";
    note: string | undefined;
    payment_intent_id?: string;
    qr_code_id?: string;
  }) => void;
}

const CHANNEL_LABELS: Record<"osobni" | "email" | "whatsapp" | "telefon", string> = {
  osobni: "Osobně",
  email: "E-mail",
  whatsapp: "WhatsApp",
  telefon: "Telefonicky (slovní popis)",
};

export function PushAssistDetailCard({
  detail,
  paymentIntentId,
  amountKc,
  qr,
  uploadError,
  generateError,
  uploading,
  generating,
  withdrawn,
  onUploadConsent,
  onGenerateQr,
  onLogDelivery,
}: PushAssistDetailCardProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [channel, setChannel] = useState<"osobni" | "email" | "whatsapp" | "telefon">("osobni");
  const [note, setNote] = useState<string>("");

  const consentBadge = detail.has_valid_paper_consent ? (
    <Badge color="success">Souhlas OK</Badge>
  ) : (
    <Badge color="warning">Souhlas chybí</Badge>
  );

  const canGenerate = detail.has_valid_paper_consent && !!paymentIntentId && !generating;
  const canDeliver = detail.has_valid_paper_consent && !!qr;

  const disclosure = amountKc !== undefined ? formatCzkWithDisclosure(amountKc) : null;

  function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
  }

  function handleUploadClick() {
    if (!selectedFile) return;
    onUploadConsent(selectedFile);
    setSelectedFile(null);
  }

  function handleGenerateClick() {
    if (!paymentIntentId) return;
    onGenerateQr(paymentIntentId);
  }

  function handleDeliveryClick() {
    if (!qr) return;
    onLogDelivery({
      delivery_channel: channel,
      note: note.trim() === "" ? undefined : note.trim(),
      payment_intent_id: paymentIntentId,
      qr_code_id: qr.qr_code_id,
    });
    setNote("");
  }

  return (
    <Card data-testid="push-assist-detail-card">
      <CardHeader>
        <div className="flex items-center gap-3 flex-wrap">
          <strong data-testid="push-assist-display-name">{detail.display_name}</strong>
          {consentBadge}
          <Badge color="blue">{detail.role}</Badge>
        </div>
        <p className="m-0">{detail.email}</p>
      </CardHeader>
      <CardBody>
        {withdrawn && (
          <div data-testid="push-assist-consent-withdrawn-banner">
            <InlineAlert variant="warning" title="Souhlas odvolán">
              Generování QR je zablokované, dokud nenahrajete nový papírový souhlas.
            </InlineAlert>
          </div>
        )}

        <section aria-labelledby="step-zero-heading" style={{ marginBottom: "1rem" }}>
          <h3 id="step-zero-heading">Krok 0 — Papírový souhlas</h3>
          <p>
            Před vygenerováním QR potřebujeme naskenovaný papírový souhlas Josefa.
            Bez souhlasu generování není možné.
          </p>
          <div className="mb-4 flex flex-col gap-1">
            <label htmlFor="paper-consent-input" className="text-sm font-medium text-secondary">
              Sken souhlasu (PDF)
            </label>
            <input
              id="paper-consent-input"
              type="file"
              accept="application/pdf"
              onChange={handleFileSelected}
              data-testid="paper-consent-file-input"
              style={{ minHeight: "48px" }}
            />
          </div>
          <Button
            type="button"
            color="primary"
            size="md"
            onPress={handleUploadClick}
            isDisabled={!selectedFile || uploading}
            data-testid="paper-consent-upload-button"
          >
            {uploading ? "Nahrávám…" : "Nahrát souhlas"}
          </Button>
          {uploadError && (
            <div data-testid="paper-consent-upload-error">
              <InlineAlert variant="error" title="Chyba nahrávání">
                {uploadError}
              </InlineAlert>
            </div>
          )}
          {detail.paper_consents.length > 0 && (
            <InlineDisclosure title={`Historie souhlasů (${detail.paper_consents.length})`}>
              <ul data-testid="paper-consent-history">
                {detail.paper_consents.map((row) => (
                  <li key={row.stored_file_id}>
                    <code>{row.original_filename}</code> — {row.clamav_status} —{" "}
                    {row.created_at}
                  </li>
                ))}
              </ul>
            </InlineDisclosure>
          )}
        </section>

        <section aria-labelledby="qr-heading" style={{ marginBottom: "1rem" }}>
          <h3 id="qr-heading">QR kód pro Josefa</h3>
          <Button
            type="button"
            color="primary"
            size="lg"
            onPress={handleGenerateClick}
            isDisabled={!canGenerate}
            data-testid="push-assist-generate-qr-button"
            aria-label={
              !detail.has_valid_paper_consent
                ? "Generovat QR — nejprve nahrát papírový souhlas"
                : !paymentIntentId
                  ? "Generovat QR — vyberte úhradu"
                  : undefined
            }
          >
            {generating ? "Generuji…" : "Generovat QR"}
          </Button>
          {generateError && (
            <div data-testid="push-assist-generate-error">
              <InlineAlert variant="error" title="Chyba generování">
                {generateError}
              </InlineAlert>
            </div>
          )}
          {qr && (
            <div
              data-testid="push-assist-qr-display"
              style={{ marginTop: "0.75rem" }}
            >
              <img
                src={`data:image/png;base64,${qr.png_base64}`}
                alt="QR kód úhrady"
                width={320}
                height={320}
                style={{ display: "block" }}
              />
              <Button
                type="button"
                color="secondary"
                size="md"
                onPress={() => downloadPng(qr.png_base64, `qr-${qr.payment_intent_id}.png`)}
                data-testid="push-assist-download-qr-button"
              >
                Stáhnout PNG
              </Button>
            </div>
          )}
        </section>

        {disclosure && (
          <InlineDisclosure title={`Slovní popis částky (${disclosure.numeric})`}>
            <p data-testid="push-assist-amount-in-words" style={{ fontSize: "1.125rem" }}>
              {disclosure.inWords}
            </p>
            <p style={{ fontSize: "0.875rem" }}>
              Použij pro telefonický mód.
            </p>
          </InlineDisclosure>
        )}

        <section aria-labelledby="delivery-heading">
          <h3 id="delivery-heading">Doručení Josefovi</h3>
          <fieldset>
            <legend>Způsob doručení</legend>
            {(Object.keys(CHANNEL_LABELS) as Array<keyof typeof CHANNEL_LABELS>).map((c) => (
              <label
                key={c}
                style={{ display: "block", minHeight: "48px", padding: "0.5rem" }}
              >
                <input
                  type="radio"
                  name="delivery_channel"
                  value={c}
                  checked={channel === c}
                  onChange={() => setChannel(c)}
                  data-testid={`push-assist-channel-${c}`}
                />
                {" "}
                {CHANNEL_LABELS[c]}
              </label>
            ))}
          </fieldset>
          <div className="mb-4 flex flex-col gap-1">
            <label htmlFor="delivery-note" className="text-sm font-medium text-secondary">
              Audit poznámka (volitelná)
            </label>
            <textarea
              id="delivery-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="push-assist-delivery-note"
              style={{ width: "100%", minHeight: "48px" }}
            />
          </div>
          <Button
            type="button"
            color="primary"
            size="md"
            onPress={handleDeliveryClick}
            isDisabled={!canDeliver}
            data-testid="push-assist-log-delivery-button"
          >
            Zaznamenat doručení
          </Button>
        </section>

        {detail.deliveries.length > 0 && (
          <InlineDisclosure title={`Historie doručení (${detail.deliveries.length})`}>
            <ul data-testid="push-assist-delivery-history">
              {detail.deliveries.map((d) => (
                <li key={d.delivery_id}>
                  {d.delivered_at} — {d.delivery_channel}
                  {d.note ? ` — ${d.note}` : ""}
                </li>
              ))}
            </ul>
          </InlineDisclosure>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * InlineDisclosure — collapsible blok přenesený z legacy ui/disclosure.tsx
 * (barrel zrušen). Chování zachováno: aria-expanded toggle, +/− indikátor,
 * focus-visible ring. Na UUI tokenech (ring-1 ring-secondary, rounded-xl,
 * bg-primary, text-secondary), BEZ --ui-* / min-h-touch.
 */
function InlineDisclosure({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl bg-primary ring-1 ring-secondary">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 border-0 bg-transparent px-4 py-3.5 text-left text-md font-medium text-primary focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-ring"
      >
        <span>{title}</span>
        <span aria-hidden="true">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="px-4 pb-4 text-secondary">{children}</div>}
    </div>
  );
}

function downloadPng(base64: string, filename: string) {
  if (typeof window === "undefined") return;
  const link = document.createElement("a");
  link.href = `data:image/png;base64,${base64}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
